import { defineMessages, MessageDescriptor } from 'react-intl';

export type SortOption =
  | {
      valueText: string;
      optionText: string;
      tooltipTitle: MessageDescriptor;
      queryOrderBy: string;
      ascending: boolean;
      isDefault: boolean;
    }
  | {
      valueText: string;
      optionText: string;
      tooltipTitle: MessageDescriptor;
      queryOrderBy: Array<string>;
      ascending: Array<boolean>;
      isDefault: boolean;
    };
export type SortOptionList = Array<SortOption>;

const BE_FIELD_R_R_INTERVAL = 'r_r_interval';
const BE_FIELD_WAVEFORM_INDEX = 'waveform_index';
const BE_FIELD_ONSET_MS = 'onset_ms';
const BE_FIELD_DURATION_MS = 'duration_ms';
const BE_FIELD_ONSET_WAVEFORM_INDEX = 'onset_waveform_index';
const BE_FIELD_LEN = 'len';
const BE_FIELD_HR_AVG = 'hr_avg';
const BE_FIELD_EVENT_TIME_MS = 'event_time_ms';

const LABEL_R_R_SHORTEST = 'R-R (Shortest)';
const LABEL_R_R_LONGEST = 'R-R (Longest)';
const LABEL_TIME_OLDEST = 'Time (Oldest)';
const LABEL_DURATION_LONGEST = 'Duration (Longest)';
const LABEL_AVG_HR_FASTEST = 'Avg HR (Fastest)';
const LABEL_BEAT_COUNT = 'Beat (Count)';

const TOOLTIP_TITLE_MAP = defineMessages({
  [LABEL_R_R_SHORTEST]: {
    id: 'sortOrder-tooltip-title-rRShortest',
    description: 'R-R 간격 좁은 순으로 정렬됨 / Sorted shortest to longest',
    defaultMessage: 'R-R 간격 좁은 순으로 정렬됨',
  },
  [LABEL_R_R_LONGEST]: {
    id: 'sortOrder-tooltip-title-rRLongest',
    description: 'R-R 간격 넓은 순으로 정렬됨 / Sorted longest to shortest',
    defaultMessage: 'R-R 간격 넓은 순으로 정렬됨',
  },
  [LABEL_TIME_OLDEST]: {
    id: 'sortOrder-tooltip-title-timeOldest',
    description: '시간 흐름 순으로 정렬됨 / Sorted oldest to newest',
    defaultMessage: '시간 흐름 순으로 정렬됨',
  },
  [LABEL_DURATION_LONGEST]: {
    id: 'sortOrder-tooltip-title-durationLongest',
    description: 'Duration 긴 순으로 정렬됨 / Sorted longest to shortest',
    defaultMessage: 'Duration 긴 순으로 정렬됨',
  },
  [LABEL_AVG_HR_FASTEST]: {
    id: 'sortOrder-tooltip-title-avgHrFastest',
    description: 'Avg HR 빠른 순을 정렬됨 / Sorted Fastest to slowest',
    defaultMessage: 'Avg HR 빠른 순을 정렬됨',
  },
  [LABEL_BEAT_COUNT]: {
    id: 'sortOrder-tooltip-title-beatCount',
    description: 'Beat 개수 많은 순으로 정렬됨 / Sorted by number of beats',
    defaultMessage: 'Beat 개수 많은 순으로 정렬됨',
  },
});

// :: HR Review ::
export const BPM_SORT_DEFAULT: SortOption = {
  valueText: LABEL_TIME_OLDEST.split(' (')[0], // 'Time'
  optionText: LABEL_TIME_OLDEST,
  tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_TIME_OLDEST],
  queryOrderBy: [BE_FIELD_WAVEFORM_INDEX],
  ascending: [true],
  isDefault: true,
};
export const BPM_SORT_OPTION_LIST: SortOptionList = [
  {
    valueText: LABEL_R_R_SHORTEST.split(' (')[0], // 'R-R'
    optionText: LABEL_R_R_SHORTEST,
    tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_R_R_SHORTEST],
    queryOrderBy: [BE_FIELD_R_R_INTERVAL, BE_FIELD_WAVEFORM_INDEX],
    ascending: [true, true],
    isDefault: false,
  },
  {
    valueText: LABEL_R_R_LONGEST.split(' (')[0], // 'R-R'
    optionText: LABEL_R_R_LONGEST,
    tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_R_R_LONGEST],
    queryOrderBy: [BE_FIELD_R_R_INTERVAL, BE_FIELD_WAVEFORM_INDEX],
    ascending: [false, true],
    isDefault: false,
  },
  BPM_SORT_DEFAULT,
];

// :: Time Event ::
export const AF_SORT_DEFAULT: SortOption = {
  valueText: LABEL_TIME_OLDEST.split(' (')[0], // 'Time'
  optionText: LABEL_TIME_OLDEST,
  tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_TIME_OLDEST],
  queryOrderBy: BE_FIELD_ONSET_MS,
  ascending: true,
  isDefault: true,
};
export const AF_SORT_OPTION_LIST: SortOptionList = [
  AF_SORT_DEFAULT,
  {
    valueText: LABEL_DURATION_LONGEST.split(' (')[0], // 'Duration'
    optionText: LABEL_DURATION_LONGEST,
    tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_DURATION_LONGEST],
    queryOrderBy: `-${BE_FIELD_DURATION_MS},${BE_FIELD_ONSET_MS}`,
    ascending: false,
    isDefault: false,
  },
  {
    valueText: LABEL_AVG_HR_FASTEST.split(' (')[0], // 'Avg HR'
    optionText: LABEL_AVG_HR_FASTEST,
    tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_AVG_HR_FASTEST],
    queryOrderBy: `-${BE_FIELD_HR_AVG},${BE_FIELD_ONSET_MS}`,
    ascending: false,
    isDefault: false,
  },
];

export const PAUSE_SORT_DEFAULT: SortOption = {
  valueText: LABEL_TIME_OLDEST.split(' (')[0], // 'Time'
  optionText: LABEL_TIME_OLDEST,
  tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_TIME_OLDEST],
  queryOrderBy: BE_FIELD_ONSET_MS,
  ascending: true,
  isDefault: true,
};
export const PAUSE_SORT_OPTION_LIST: SortOptionList = [
  PAUSE_SORT_DEFAULT,
  {
    valueText: LABEL_DURATION_LONGEST.split(' (')[0], // 'Duration'
    optionText: LABEL_DURATION_LONGEST,
    tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_DURATION_LONGEST],
    queryOrderBy: `-${BE_FIELD_DURATION_MS},${BE_FIELD_ONSET_MS}`,
    ascending: false,
    isDefault: false,
  },
];

export const OTHERS_SORT_DEFAULT: SortOption = {
  valueText: LABEL_TIME_OLDEST.split(' (')[0], // 'Time'
  optionText: LABEL_TIME_OLDEST,
  tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_TIME_OLDEST],
  queryOrderBy: BE_FIELD_ONSET_MS,
  ascending: true,
  isDefault: true,
};
export const OTHERS_SORT_OPTION_LIST: SortOptionList = [OTHERS_SORT_DEFAULT];

// :: Ectopic ::
export const SVT_SORT_DEFAULT: SortOption = {
  valueText: LABEL_TIME_OLDEST.split(' (')[0], // 'Time'
  optionText: LABEL_TIME_OLDEST,
  tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_TIME_OLDEST],
  queryOrderBy: BE_FIELD_ONSET_WAVEFORM_INDEX,
  ascending: true,
  isDefault: true,
};
export const SVT_SORT_OPTION_LIST: SortOptionList = [
  SVT_SORT_DEFAULT,
  {
    valueText: LABEL_BEAT_COUNT.split(' (')[0], // 'Beat'
    optionText: LABEL_BEAT_COUNT,
    tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_BEAT_COUNT],
    queryOrderBy: `-${BE_FIELD_LEN},${BE_FIELD_ONSET_WAVEFORM_INDEX}`,
    ascending: false,
    isDefault: false,
  },
  {
    valueText: LABEL_AVG_HR_FASTEST.split(' (')[0], // 'Avg HR'
    optionText: LABEL_AVG_HR_FASTEST,
    tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_AVG_HR_FASTEST],
    queryOrderBy: `-${BE_FIELD_HR_AVG},${BE_FIELD_ONSET_WAVEFORM_INDEX}`,
    ascending: false,
    isDefault: false,
  },
];

export const VT_SORT_DEFAULT: SortOption = {
  valueText: LABEL_TIME_OLDEST.split(' (')[0], // 'Time'
  optionText: LABEL_TIME_OLDEST,
  tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_TIME_OLDEST],
  queryOrderBy: BE_FIELD_ONSET_WAVEFORM_INDEX,
  ascending: true,
  isDefault: true,
};
export const VT_SORT_OPTION_LIST: SortOptionList = [
  VT_SORT_DEFAULT,
  {
    valueText: LABEL_BEAT_COUNT.split(' (')[0], // 'Beat'
    optionText: LABEL_BEAT_COUNT,
    tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_BEAT_COUNT],
    queryOrderBy: `-${BE_FIELD_LEN},${BE_FIELD_ONSET_WAVEFORM_INDEX}`,
    ascending: false,
    isDefault: false,
  },
  {
    valueText: LABEL_AVG_HR_FASTEST.split(' (')[0], // 'Avg HR'
    optionText: LABEL_AVG_HR_FASTEST,
    tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_AVG_HR_FASTEST],
    queryOrderBy: `-${BE_FIELD_HR_AVG},${BE_FIELD_ONSET_WAVEFORM_INDEX}`,
    ascending: false,
    isDefault: false,
  },
];

export const APC_SORT_DEFAULT: SortOption = {
  valueText: LABEL_TIME_OLDEST.split(' (')[0], // 'Time'
  optionText: LABEL_TIME_OLDEST,
  tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_TIME_OLDEST],
  queryOrderBy: BE_FIELD_ONSET_WAVEFORM_INDEX,
  ascending: true,
  isDefault: true,
};
export const APC_SORT_OPTION_LIST: SortOptionList = [APC_SORT_DEFAULT];

export const VPC_SORT_DEFAULT: SortOption = {
  valueText: LABEL_TIME_OLDEST.split(' (')[0], // 'Time'
  optionText: LABEL_TIME_OLDEST,
  tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_TIME_OLDEST],
  queryOrderBy: BE_FIELD_ONSET_WAVEFORM_INDEX,
  ascending: true,
  isDefault: true,
};
export const VPC_SORT_OPTION_LIST: SortOptionList = [VPC_SORT_DEFAULT];

export const PTE_SORT_DEFAULT: SortOption = {
  valueText: LABEL_TIME_OLDEST.split(' (')[0], // 'Time'
  optionText: LABEL_TIME_OLDEST,
  tooltipTitle: TOOLTIP_TITLE_MAP[LABEL_TIME_OLDEST],
  queryOrderBy: BE_FIELD_EVENT_TIME_MS,
  ascending: true,
  isDefault: true,
};
export const PTE_SORT_OPTION_LIST: SortOptionList = [PTE_SORT_DEFAULT];
